import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const SubscriptionDetailModal = ({ isOpen, onClose, subscription, onCancel, onReactivate }) => {
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!isOpen || !subscription?.id) return;

    let cancelled = false;
    setInvoices([]);
    setInvoicesError(null);
    setHasMore(false);
    setNextCursor(null);
    setInvoicesLoading(true);

    supabase.functions
      .invoke('list-invoices', { body: { subscriptionId: subscription.id } })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) throw error;
        setInvoices(data?.invoices ?? []);
        setHasMore(data?.hasMore ?? false);
        setNextCursor(data?.nextCursor ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch invoices:', err);
        setInvoicesError('Failed to load billing history.');
      })
      .finally(() => {
        if (!cancelled) setInvoicesLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, subscription?.id]);

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;

    setLoadingMore(true);
    setInvoicesError(null);

    try {
      const { data, error } = await supabase.functions.invoke('list-invoices', {
        body: { subscriptionId: subscription.id, startingAfter: nextCursor },
      });
      if (error) throw error;
      setInvoices((prev) => [...prev, ...(data?.invoices ?? [])]);
      setHasMore(data?.hasMore ?? false);
      setNextCursor(data?.nextCursor ?? null);
    } catch (err) {
      console.error('Failed to load more invoices:', err);
      setInvoicesError('Failed to load more invoices. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, subscription?.id]);

  if (!isOpen) return null;

  const statusBadgeClass = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300';
      case 'open':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300';
      case 'void':
      case 'uncollectible':
        return 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Subscription Details</h3>
            <p className="text-sm text-muted-foreground mt-1">{subscription?.id}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-smooth"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Customer Information */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="User" size={16} />
              Customer Information
            </h4>
            <div className="bg-muted/30 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Name</span>
                <span className="text-foreground font-medium">{subscription?.customerName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Email</span>
                <span className="text-foreground font-medium">{subscription?.customerEmail}</span>
              </div>
            </div>
          </div>

          {/* Subscription Details */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="CreditCard" size={16} />
              Subscription Details
            </h4>
            <div className="bg-muted/30 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Plan</span>
                <span className="text-foreground font-medium">{subscription?.plan}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Amount</span>
                <span className="text-foreground font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: subscription?.currency
                  })?.format(subscription?.amount)} / {subscription?.interval}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <span className="text-foreground font-medium capitalize">{subscription?.status?.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Created</span>
                <span className="text-foreground font-medium">{new Date(subscription?.createdAt)?.toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Billing Period */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Calendar" size={16} />
              Current Billing Period
            </h4>
            <div className="bg-muted/30 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Period Start</span>
                <span className="text-foreground font-medium">{new Date(subscription?.currentPeriodStart)?.toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Period End</span>
                <span className="text-foreground font-medium">{new Date(subscription?.currentPeriodEnd)?.toLocaleDateString()}</span>
              </div>
              {subscription?.nextBillingDate && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Next Billing</span>
                  <span className="text-foreground font-medium">{new Date(subscription?.nextBillingDate)?.toLocaleDateString()}</span>
                </div>
              )}
              {subscription?.trialEnd && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Trial Ends</span>
                  <span className="text-foreground font-medium">{new Date(subscription?.trialEnd)?.toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Billing History */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="FileText" size={16} />
              Billing History
            </h4>

            {invoicesLoading ? (
              <div className="flex items-center justify-center py-8 bg-muted/30 rounded-lg">
                <Icon name="Loader2" size={20} className="animate-spin text-muted-foreground mr-2" />
                <span className="text-sm text-muted-foreground">Loading invoices…</span>
              </div>
            ) : invoicesError && invoices.length === 0 ? (
              <div className="flex items-center gap-2 py-4 px-4 bg-destructive/10 text-destructive rounded-lg text-sm">
                <Icon name="AlertCircle" size={16} />
                {invoicesError}
              </div>
            ) : invoices.length === 0 ? (
              <div className="py-6 text-center bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">No invoices found for this subscription.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {invoices.map((invoice) => (
                  <div key={invoice.id} className="bg-muted/30 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon name="Receipt" size={16} className="text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: invoice.currency ?? subscription?.currency ?? 'USD'
                          }).format(invoice.amount)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {invoice.date ? new Date(invoice.date).toLocaleDateString() : '—'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-md ${statusBadgeClass(invoice.status)}`}>
                        {invoice.status}
                      </span>
                      {invoice.invoiceUrl ? (
                        <a
                          href={invoice.invoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="ghost" size="sm" iconName="Download">
                            Invoice
                          </Button>
                        </a>
                      ) : invoice.hostedInvoiceUrl ? (
                        <a
                          href={invoice.hostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="ghost" size="sm" iconName="ExternalLink">
                            View Invoice
                          </Button>
                        </a>
                      ) : (
                        <div className="relative group flex items-center">
                          <Button variant="ghost" size="sm" iconName="Download" disabled className="opacity-40 cursor-not-allowed">
                            Invoice
                          </Button>
                          <Icon name="Info" size={13} className="ml-1 text-muted-foreground/60 cursor-default" />
                          <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-10 pointer-events-none">
                            <div className="bg-popover border border-border text-popover-foreground text-xs rounded-md px-3 py-1.5 shadow-lg whitespace-nowrap">
                              PDF not available for this invoice
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {invoicesError && (
                  <div className="flex items-center gap-2 py-3 px-4 bg-destructive/10 text-destructive rounded-lg text-sm">
                    <Icon name="AlertCircle" size={16} />
                    {invoicesError}
                  </div>
                )}

                {hasMore && (
                  <div className="pt-2 flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      iconName={loadingMore ? 'Loader2' : 'ChevronDown'}
                    >
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-muted/30 flex items-center justify-between">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <div className="flex gap-2">
            {(subscription?.status === 'active' || subscription?.status === 'trialing') && !subscription?.cancelAtPeriodEnd && (
              <Button
                variant="destructive"
                iconName="XCircle"
                onClick={() => {
                  onCancel(subscription?.id);
                  onClose();
                }}
              >
                Cancel Subscription
              </Button>
            )}
            {subscription?.cancelAtPeriodEnd && subscription?.status !== 'canceled' && (
              <Button
                iconName="RefreshCw"
                onClick={() => {
                  onReactivate(subscription?.id);
                  onClose();
                }}
              >
                Reactivate
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDetailModal;
